import type TRTC from 'trtc-sdk-v5'

let client = {} as TRTC
export const useTrtcStore = defineStore({
  id: 'trtc',
  state: () => ({
    trtcAppid: 0 as number, // appId
    userSig: '' as string, // 签名
    privateMapKey: '' as string, // 进房钥匙
    privateMapKeyMin: '' as string, // 上麦进房钥匙
    role: '' as 'host' | 'audience' | '', // 直播角色 host:主播，audience:观众
    mode: 'live' as 'live' | 'rtc', // 场景值 live:直播,rtc: 通话
    remoteUsers: {} as {
      [prop: string]: {
        elId: string
        uid: string | number
        streamType: 'main' | 'screen' | ''
      }
    }, // 远程用户、主播  elId：标签ID，uid，streamType：主流或辅流
    joined: false as boolean, // 是否跑完整个流程进房或发布
    isJoin: false as boolean, // 是否有调进入方法
    initedMicrophone: false as boolean // 是否已经初始化麦克风
  }),
  actions: {
    //#region 获取腾讯签名参数相关
    // 设置腾讯签名参数
    setTrtcSDKParams(appId: number, userSig: string, privateMapKey: string) {
      this.trtcAppid = appId
      this.userSig = userSig
      this.privateMapKey = privateMapKey
    },
    // 设置腾讯上麦签名参数
    setTrtcSDKMinParams(privateMapKey: string) {
      this.privateMapKeyMin = privateMapKey
    },
    /**
     * 设置SDKAppId
     */
    setTrtcAppId(value: number) {
      this.trtcAppid = value
    },
    /**
     * 设置socket返回的agoraKey
     */
    setUserSig(key: string) {
      this.userSig = key
    },
    /**
     * 设置socket返回的agoraKey
     */
    setPrivateMapKey(key: string) {
      this.privateMapKey = key
    },
    //#endregion

    //#region 腾讯SDK 获取实例
    // 创建实例
    async create() {
      console.log('TrtcSDK => 创建实例')
      const roomStore = useRoomStore()
      // 视频的房间才显示遮罩层
      this.setIsShowMask(true)

      // 根据房间类型获取场景值
      const mode =
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ? 'rtc' : 'live'

      this.mode = mode

      if (this.isJoin) {
        await this.exit()
      }

      client = window.TRTC.create()
    },
    //  加入频道
    async join() {
      const Trtc = window.TRTC
      const i18n = useNuxtApp().$i18n
      const roomStore = useRoomStore()
      const userStore = useUserStore()
      const audioStore = useAudioStore()
      // 视频房、通话房才开启定时器
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsTimeout() //开启定时器
      }

      try {
        this.isJoin = true
        // 进入频道
        // 用户在麦上就切换角色
        await this.setRole(audioStore.isOnMicSeat ? 'host' : 'audience')
        const sdkAppId = Number(this.trtcAppid)
        const roomId = Number(roomStore.roomId)
        const userSig = this.userSig
        const privateMapKey =
          this.role === 'host' ? this.privateMapKeyMin : this.privateMapKey
        const userId = userStore.userInfo.uid.toString()

        // 根据场景值进房
        if (this.mode === 'live') {
          await client.enterRoom({
            sdkAppId,
            roomId,
            userSig,
            userId,
            privateMapKey,
            scene: Trtc.TYPE.SCENE_LIVE,
            role:
              this.role === 'host'
                ? Trtc.TYPE.ROLE_ANCHOR
                : Trtc.TYPE.ROLE_AUDIENCE,
            enableAutoPlayDialog: false, // 是否开启 SDK 自动播放失败弹窗,设false 监听事件来用自己的弹窗
            autoReceiveAudio: false // 是否自动接收音频。
          })
        }
        if (this.mode === 'rtc') {
          await client.enterRoom({
            sdkAppId,
            roomId,
            userSig,
            userId,
            privateMapKey,
            scene: Trtc.TYPE.SCENE_RTC,
            enableAutoPlayDialog: false,
            autoReceiveAudio: false // 是否自动接收音频。
          })
        }
        console.log(
          'TrtcSDK => 加入频道成功',
          roomStore.roomId.toString(),
          userStore.userInfo.uid,
          new Date(),
          new Date().getTime()
        )

        // 语音房启用 监听远程用户
        if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
          client.on(
            Trtc.EVENT.REMOTE_AUDIO_AVAILABLE,
            this.onRemoteAudioAvailable
          ) // 发布
          client.on(
            Trtc.EVENT.REMOTE_AUDIO_UNAVAILABLE,
            this.onRemoteAudioUnavailable
          ) // 取消发布
          // 音量回调事件
          client.on(Trtc.EVENT.AUDIO_VOLUME, this.onAudioVolume)

          // 开启音量回调，并设置每 1500ms 触发一次事件
          client.enableAudioVolumeEvaluation(750)
        }

        // 视频的房间启用，视频直播和1v1启用 监听远程用户
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
        ) {
          // 当远端用户成功发布视频轨道之后
          client.on(
            Trtc.EVENT.REMOTE_VIDEO_AVAILABLE,
            this.onRemoteVideoAvailable
          )
          // 当远端用户取消发布视频轨道
          client.on(
            Trtc.EVENT.REMOTE_VIDEO_UNAVAILABLE,
            this.onRemoteVideoUnavailable
          )
        }

        // 自动播放失败事件,因点击后没有自动播放，暂时用SDK的弹窗
        client.on(Trtc.EVENT.AUTOPLAY_FAILED, () => {
          // 引导用户点击页面，可以弹窗提示。
          // if (!common.checkVersion().app) {
          showConfirmDialog({
            message: i18n.t('room.AtuoPlayFailedMsg'),
            confirmButtonText: i18n.t('public.Confirm'),
            showCancelButton: false,
            className: 'private-dialog-customize single-btn'
          }).catch((error) => {
            console.log(error)
          })
          // }
        })

        // 通话房的采集设备播放
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
          this.mode === 'rtc'
        ) {
          await client.startLocalAudio()
          await client
            .startLocalVideo({
              view: 'trtcFloatingVideo',
              option: {
                profile: {
                  width: 270,
                  height: 480,
                  frameRate: 15,
                  bitrate: 400
                }
              }
            })
            .then(() => {
              console.log('TrtcSDK => 发布成功')
              // 发布成功，判断清除发布超时
              roomStore.closeIsTimeout({ isPublishTimeout: true })
            })
        }

        // 语音房初始化时 如果用户自己在麦位上，标签是主播角色就发布本地音频轨道
        if (
          roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
          this.role === 'host' &&
          audioStore.isOnMicSeat
        ) {
          const micInfo = audioStore.queueList.find(
            (item) => item.value.member?.uid === userStore.userInfo.uid
          )?.value.mic_info
          this.manageLocalAudioMicrophone(!micInfo?.micState)
        }

        this.joined = true
        // 如果还没此状态还没更新，就更新
        if (roomStore.isShowMask && this.mode === 'live') {
          this.setIsShowMask(roomStore.isHostLive)
        }
      } catch (error) {
        console.log('TrtcSDK => 进房函数报错', error)
        this.isJoin = false
      }
    },
    /**
     * 设置角色
     */
    async setRole(value: 'host' | 'audience') {
      // 不同值才设置
      if (this.role !== value) {
        console.log('TrtcSDK => 开始设置角色', value)
        this.role = value
        // 如果是主播
        if (this.role == 'host') {
          // 如果没有上麦key就获取
          if (!this.privateMapKeyMin) {
            const roomStore = useRoomStore()
            await roomStore.getSDKMinToken()
          }
          // 如果进房成功
          if (this.joined) {
            const Trtc = window.TRTC
            await client.switchRole(Trtc.TYPE.ROLE_ANCHOR, {
              privateMapKey: this.privateMapKeyMin
            })
            console.log('TrtcSDK => 设置主播角色成功', value)
          }
        } else if (this.role === 'audience') {
          // 如果进房成功
          if (this.joined) {
            const Trtc = window.TRTC
            await client.switchRole(Trtc.TYPE.ROLE_AUDIENCE, {
              privateMapKey: this.privateMapKey
            })
            console.log('TrtcSDK => 设置观众角色成功', value)
          }
        }
      }
    },
    /**
     * 当远端用户成功发布音视频轨道之后函数
     * TRTC 有主路视频流（主流）和辅路视频流（辅流）streamType
     * 摄像头通过主流发布，屏幕分享通过辅流发布。
     */
    async onRemoteVideoAvailable({ userId, streamType }) {
      console.log(
        'TrtcSDK => 远程用户',
        userId,
        streamType,
        new Date(),
        new Date().getTime()
      )
      const roomStore = useRoomStore()
      // 视频房或通话房目前只接收主播的流，如果不是主播的流就return
      if (
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL) &&
        roomStore.roomUid != userId
      ) {
        console.log('TrtcSDK => 远程用户,不是主播的流', userId, streamType)
        return
      }

      // 视频的房间才显示遮罩层
      this.setIsShowMask(true)

      // 主路视频流，一般是推摄像头的那路流
      this.remoteUsers[userId] = {
        elId: 'trtc_user_' + userId,
        uid: userId,
        streamType
      }
      // 视频的房 开始播放视频
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        // 等待一下取元素显示直播
        await nextTick()
        client.muteRemoteAudio(userId, false) // 调此API播放音频
        // 开始播放视频
        await client
          .startRemoteVideo({
            userId,
            streamType,
            view: this.remoteUsers[userId].elId
          })
          .then(() => {
            console.log('TrtcSDK => 显示回调', new Date(), new Date().getTime())
            // 监听远程用户
            this.remoteUsersChange(false)

            this.setIsShowMask(false)

            // 发布成功，判断清除发布超时
            roomStore.closeIsTimeout({ isPirstFrameTimeout: true })

            if (this.mode === 'rtc') {
              // 兼容安卓，让安卓移除遮挡层
              // const socketStore = useSocketStore()
              // socketStore.sendMessage()
              const callStore = useCallStore()
              callStore.onStartBillingNew()
            }
          })
      }
    },
    /**
     * 当远端用户取消发布音视频轨道
     * TRTC 有主路视频流（主流）和辅路视频流（辅流）streamType
     * 摄像头通过主流发布，屏幕分享通过辅流发布。
     */
    async onRemoteVideoUnavailable({ userId, streamType }) {
      console.log('TrtcSDK => 离开的远程用户', userId, streamType)
      delete this.remoteUsers[userId]

      this.remoteUsersChange(false) // 监听远程用户
      // trtc.stopRemoteVideo({ userId, streamType })
    },
    /**
     * 当远端用户成功发布音频轨道之后函数
     */
    async onRemoteAudioAvailable({ userId }) {
      console.log('TrtcSDK => 远程用户', userId)
      const audioStore = useAudioStore()
      // 主路视频流，一般是推摄像头的那路流
      this.remoteUsers[userId] = {
        elId: '',
        uid: userId,
        streamType: ''
      }
      await nextTick()
      console.log('---------------------------', audioStore.isOpenSound)
      audioStore.isOpenSound && client.muteRemoteAudio(userId, false) // 调此API播放音频

      // 监听远程用户
      this.remoteUsersChange(false)
    },
    /**
     * 当远端用户取消发布音轨道
     */
    async onRemoteAudioUnavailable({ userId }) {
      console.log('TrtcSDK => 离开的远程用户', userId)
      client.muteRemoteAudio(userId, true) // 退出要取消拉流音该用户
      delete this.remoteUsers[userId]

      this.remoteUsersChange(false) // 监听远程用户
    },
    /**
     * 所有人音量大小事件
     */
    onAudioVolume(event) {
      const audioStore = useAudioStore()
      // 传参设置音量
      audioStore.setVolumeIndicator(event.result)
    },
    /**
     * 语音房使用
     * 管理本地上下麦克风
     * @param {boolean} isPut true:上麦, false:下麦
     */
    async manageLocalAudioMicrophone(isPut: boolean) {
      console.log('TrtcSDK => 语音房管理麦克风', isPut)
      // 操作麦克风就切换主播角色,上麦先切换身份再推流，下麦先断流再切身份
      if (isPut) {
        await this.setRole('host')
        client
          .startLocalAudio()
          .then(() => {
            this.initedMicrophone = true
          })
          .catch((err) => {
            if (JSON.stringify(err).indexOf('DEVICE_ERROR') != -1) {
              const audioStore = useAudioStore()
              // 麦克风图标显示
              audioStore.setIsOpenMic(false)
              const agoraStore = useAgoraStore()
              agoraStore.setAuthPopupConfig(true)
            }
          })
      } else {
        await client.stopLocalAudio()
        this.setRole('audience')
      }
    },
    /**
     * 语音房使用
     * 开关远程所有音频轨道
     * @param {boolean} isPlay true:播放, false:不播放
     */
    switchAllRemoteAudioTrack(isPlay: boolean) {
      console.log('TrtcSDK => 语音房是否播放音频', isPlay)
      client.muteRemoteAudio('*', !isPlay)
    },
    // room遮罩层设置
    setIsShowMask(show: boolean) {
      const roomStore = useRoomStore()
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsShowMask(show)
      }
    },
    /**
     * 监听远程用户
     * @param {boolean} isInit 是否初始化
     */
    remoteUsersChange(init: boolean) {
      const roomStore = useRoomStore()
      roomStore.whetherHostIsLive(init ? [] : Object.keys(this.remoteUsers)) // 监听远程用户
    },
    // 离开
    async exit() {
      const roomStore = useRoomStore()
      // 强制退出才清除
      if (
        roomStore.quitRoomType === 0 ||
        roomStore.quitRoomType === 1 ||
        roomStore.quitRoomType === 3
      ) {
        const Trtc = window.TRTC

        // 注销监听事件
        // 音频房
        if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
          client.off(
            Trtc.EVENT.REMOTE_AUDIO_AVAILABLE,
            this.onRemoteAudioAvailable
          )
          client.off(
            Trtc.EVENT.REMOTE_AUDIO_UNAVAILABLE,
            this.onRemoteAudioUnavailable
          ) // 取消发布
          // 音量回调事件
          client.off(Trtc.EVENT.AUDIO_VOLUME, this.onAudioVolume)
          // 开启音量回调，并设置每 1500ms 触发一次事件
          client.enableAudioVolumeEvaluation(750)
        }
        // 视频房
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
        ) {
          client.off(
            Trtc.EVENT.REMOTE_VIDEO_AVAILABLE,
            this.onRemoteVideoAvailable
          )
          client.off(
            Trtc.EVENT.REMOTE_VIDEO_UNAVAILABLE,
            this.onRemoteVideoUnavailable
          )
        }

        this.remoteUsersChange(true) // 初始化

        // 如需关闭音量回调，传入 interval 值小于等于0即可
        client.enableAudioVolumeEvaluation(-1)

        // 退出房间和关闭摄像头和麦克风
        client.stopLocalAudio()
        client.stopLocalVideo()
        await client.exitRoom()

        // 销毁
        client.destroy()
        this.resetData()
        console.log('TrtcSDK => 销毁成功')
      }
    },
    //#endregion

    // 重置数据
    resetData() {
      const audioStore = useAudioStore()
      this.isJoin = false
      this.joined = false
      this.remoteUsers = {}
      audioStore.setVolumeIndicator([])
      this.trtcAppid = 0
      this.userSig = ''
      this.privateMapKey = ''
      this.privateMapKeyMin = ''
      this.role = 'audience'
    }
  }
})
